import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { trackPageView } from '@/services/usageTracker';
import { useAuthStore } from '@/stores/auth';
import { useProviderAuthStore } from '@/stores/providerAuth';
import { recoverFromChunkError } from '@/composables/useAppUpdate';
import { api } from '@/services/api';

// Guard del panel del proveedor (S3 P1) — sesión SEPARADA de staff/tutor. Si no
// hay sesión de proveedor (ni token en sessionStorage recuperable), redirige a
// /proveedor/ingresar preservando el destino.
async function providerAuthGuard(to: { fullPath: string }) {
  const providerAuth = useProviderAuthStore();
  if (!providerAuth.bootstrapped) await providerAuth.bootstrap();
  if (!providerAuth.isAuthenticated) {
    return { path: '/proveedor/ingresar', query: { redirect: to.fullPath } };
  }
  return true;
}

// Gate de ruta del CRM (hardening post-E2E 2026-09-22): el sidebar ya esconde el
// grupo si feature.crm está OFF, pero un deep-link renderizaba la vista igual.
// RLS protege la data (el backend aísla por clinic_id); este guard alinea la
// RUTA con el flag para consistencia de producto. Cache 60s para no pegarle al
// endpoint en cada navegación interna del CRM.
let crmFlagCache: { value: boolean; at: number } | null = null;
async function crmFlagEnabled(): Promise<boolean> {
  if (crmFlagCache && Date.now() - crmFlagCache.at < 60_000) return crmFlagCache.value;
  try {
    const { data } = await api.get<{ enabled: boolean }>('/flags/feature.crm');
    crmFlagCache = { value: !!data.enabled, at: Date.now() };
  } catch {
    crmFlagCache = { value: false, at: Date.now() };
  }
  return crmFlagCache.value;
}
async function crmRouteGuard() {
  return (await crmFlagEnabled()) ? true : { path: '/app/bandeja' };
}

// Exportado para tests (guarda anti-enlace-roto de las acciones de KPI del
// tablero: cada action.to debe existir como ruta de /app). Ver widgetCatalog.spec.
export const appChildren: RouteRecordRaw[] = [
  // D74+5: la Bandeja de Nova es la landing post-login (decisión CEO).
  // La vista "Hoy" se eliminó; /app/hoy queda como redirect para bookmarks.
  { path: '', redirect: 'bandeja' },
  { path: 'hoy', redirect: 'bandeja' },
  // Sprint S3 — tablero personalizable: el cliente arma su vista eligiendo
  // widgets de un catálogo curado (grid-layout-plus + persistencia local MVP).
  { path: 'mis-tableros', component: () => import('@/views/app/MisTablerosView.vue') },
  // §WF 2026-09-07 — vistas clínicas (agenda/visita/pacientes/inventario): una
  // clínica en "modo financiero" (is_financial_only, sin CRM/visitas) las vería
  // vacías con "Sin datos". `meta.blockedForFinancialOnly` hace que el guard la
  // redirija a /app/modo-financiero con una explicación clara.
  { path: 'agenda', component: () => import('@/views/app/AgendaView.vue'), meta: { blockedForFinancialOnly: true } },
  { path: 'visita', component: () => import('@/views/app/VisitaView.vue'), meta: { blockedForFinancialOnly: true } },
  // D73: las 5 vistas de análisis heredadas (Gerencial, Operación, Negocio,
  // Crecimiento, Finanzas) se eliminaron — su contenido vive como widgets en
  // "Mis Tableros". Estas rutas quedan como redirects para no romper
  // bookmarks/deep-links viejos.
  { path: 'gerencial', redirect: 'mis-tableros' },
  { path: 'negocio', redirect: 'mis-tableros' },
  { path: 'finanzas', redirect: 'mis-tableros' },
  { path: 'crecimiento', redirect: 'mis-tableros' },
  { path: 'operacion', redirect: 'mis-tableros' },
  { path: 'operacion-v2', redirect: 'mis-tableros' },
  // D69+1 Frente A / Parte C: apertura de ingreso NETO por tipo × sociedad.
  // D69+2: el tablero se unificó como sección de Gerencial (decisión CEO); esta
  // ruta se conserva sólo como deep-link (bookmarks), sin entrada en sidebar.
  // §2.4 D72+4: es una vista 100% financiera → requiere rol con acceso financiero
  // (dueño/admin). El backend igual gatea sus endpoints (403); esto evita que un
  // staff con el bookmark aterrice en una vista vacía/rota.
  { path: 'ingreso-por-tipo', component: () => import('@/views/app/IngresoPorTipoView.vue'), meta: { requiresFinancialAccess: true } },
  { path: 'pacientes', component: () => import('@/views/app/PacientesView.vue'), meta: { blockedForFinancialOnly: true } },
  { path: 'inventario', component: () => import('@/views/app/InventarioView.vue'), meta: { blockedForFinancialOnly: true } },
  // ── CRM port (Fase 1, PREVIEW detrás de feature.crm) — diseños portados fielmente
  // de crm.wiservet.com (Ficha + Visita). No enlazados en el sidebar todavía; el
  // grupo CRM se gatea por feature.crm. El backend ya aísla por clinic_id (JWT).
  // F15 (enforcement por rol en backend) PENDIENTE antes de encender clínica real.
  { path: 'crm/pacientes', component: () => import('@/views/crm/PacientesCrmView.vue'), beforeEnter: crmRouteGuard, meta: { blockedForFinancialOnly: true } },
  { path: 'crm/ficha/:id', component: () => import('@/views/crm/FichaPacienteCrmView.vue'), beforeEnter: crmRouteGuard, meta: { blockedForFinancialOnly: true } },
  { path: 'crm/visita/:id', component: () => import('@/views/crm/CrmVisitaView.vue'), beforeEnter: crmRouteGuard, meta: { blockedForFinancialOnly: true } },
  { path: 'crm/hoy', component: () => import('@/views/crm/HoyCrmView.vue'), beforeEnter: crmRouteGuard, meta: { blockedForFinancialOnly: true } },
  { path: 'crm/mi-clinica', component: () => import('@/views/crm/MiClinicaCrmView.vue'), beforeEnter: crmRouteGuard, meta: { blockedForFinancialOnly: true } },
  // Agenda (solo lectura, sin crear/mover cita — F15 pendiente para esa acción).
  // Port fiel del timeline vertical de crm.wiservet.com sobre GET /appointments
  // real. Ver AgendaCrmView.vue.
  { path: 'crm/agenda', component: () => import('@/views/crm/AgendaCrmView.vue'), beforeEnter: crmRouteGuard, meta: { blockedForFinancialOnly: true } },
  // Inventario (solo lectura). Port fiel del catálogo del CRM sobre
  // GET /operations/inventory-catalog(-summary) real; costo/margen se
  // ocultan en la UI para roles no financieros (redacción ya la hace el
  // backend). Ver InventarioCrmView.vue.
  { path: 'crm/inventario', component: () => import('@/views/crm/InventarioCrmView.vue'), beforeEnter: crmRouteGuard, meta: { blockedForFinancialOnly: true } },
  // Panel del Proveedor (marketplace) — PREVIEW de diseño Tier 3 (datos de EJEMPLO
  // del prototipo; backend provider self-service DIFERIDO). Solo acceso financiero.
  { path: 'marketplace-proveedor', component: () => import('@/views/marketplace/MarketplaceProviderPanelView.vue'), meta: { requiresFinancialAccess: true } },
  // §WF 2026-09-07 — destino del guard de modo financiero: explica por qué las
  // vistas clínicas no están disponibles (clínica solo-finanzas, sin CRM).
  { path: 'modo-financiero', component: () => import('@/views/app/ModoFinancieroView.vue') },
  { path: 'bandeja', component: () => import('@/views/app/NovaBandejaView.vue') },
  // Onboarding guiado de conexión (wizard CRM → SII → Previred). Coexiste con
  // ConnectDrawer; las entradas lo usan según el flag tweaks.showConnectWizard.
  { path: 'conectar', component: () => import('@/views/app/ConectarWizardView.vue') },
  // D52a: ruta /nova-voz deprecada — la voz ahora vive dentro del chat (NovaView).
  // Mantiene backward-compat redirigiendo a /bandeja (el TopBar abre Nova overlay con mic).
  { path: 'nova-voz', redirect: 'bandeja' },
  // D63 Sprint 1 — Sección Admin consolidada: tabs internas dentro de /app/admin/.
  // Las rutas viejas /app/eventos y /app/roadmap quedan como redirects retrocompatibles.
  { path: 'eventos', redirect: '/app/admin/eventos' },
  { path: 'roadmap', redirect: '/app/admin/roadmap' },
  {
    path: 'admin',
    component: () => import('@/views/admin/AdminView.vue'),
    meta: { requiresWiservetAdmin: true },
    children: [
      { path: '', redirect: '/app/admin/eventos' },
      // Sección 1: Operación
      { path: 'eventos', component: () => import('@/views/admin/EventsView.vue') },
      { path: 'roadmap', component: () => import('@/views/app/RoadmapView.vue') },
      { path: 'etl-health', component: () => import('@/views/admin/EtlHealthView.vue') },
      { path: 'etl-cobertura', component: () => import('@/views/admin/EtlCoberturaView.vue') },
      // Sección 2: Nova IA — D64 Sprint 3 implementado
      { path: 'nova-prompts', component: () => import('@/views/admin/NovaPromptsView.vue') },
      { path: 'nova-observability', component: () => import('@/views/admin/NovaObservabilityAdminView.vue') },
      { path: 'nova-logs', component: () => import('@/views/admin/NovaLogsAdminView.vue') },
      // Sección 3: Clientes — D64 Sprint 2 implementado
      { path: 'clinicas', component: () => import('@/views/admin/ClinicasAdminView.vue') },
      { path: 'usuarios', component: () => import('@/views/admin/UsuariosAdminView.vue') },
      { path: 'leads', component: () => import('@/views/admin/LeadsAdminView.vue') },
      { path: 'whitelist', component: () => import('@/views/admin/WhitelistAdminView.vue') },
      // Marketplace (Fase B) — operador: sembrar proveedores/servicios/cupos.
      { path: 'marketplace', component: () => import('@/views/admin/MarketplaceOperatorView.vue') },
      // Sección 4: Plataforma — D64 Sprint 4 implementado
      { path: 'sistema', component: () => import('@/views/admin/SystemAdminView.vue') },
      { path: 'auditoria-legal', component: () => import('@/views/admin/AuditLegalAdminView.vue') },
      // Sección 5: Wiservet OS — métricas de plataforma multi-clínica.
      { path: 'wiservet-os', component: () => import('@/views/admin/WiservetOSView.vue') },
      // Sección 6: Academia — curso de ventas interno (HTML embebido en backend).
      { path: 'academia', component: () => import('@/views/admin/AcademiaView.vue') },
    ],
  },
  // Academia interna (curso de ventas) — acceso ACOTADO (superadmin Wiservet o
  // academyAllowlist, ej. ventas). Vista standalone, NO es el panel admin.
  {
    path: 'academia',
    component: () => import('@/views/admin/AcademiaView.vue'),
    meta: { requiresAcademyAccess: true },
  },
  // Alta de clínicas — acceso ACOTADO (superadmin Wiservet o onboarderAllowlist,
  // ej. Sofía/ventas). Vista standalone, NO es el panel admin.
  {
    path: 'nueva-clinica',
    component: () => import('@/views/admin/NuevaClinicaView.vue'),
    meta: { requiresOnboarderAccess: true },
  },
  { path: 'config', component: () => import('@/views/app/ConfigView.vue') },
  // Conciliación bancaria Fintoc × SII — solo clinic_owner/clinic_admin.
  { path: 'conciliacion', component: () => import('@/views/app/ConciliacionView.vue'), meta: { requiresFinancialAccess: true } },
  // Marketplace — panel de la clínica (ventas por paciente + comisión + invitar + embudo).
  { path: 'marketplace', component: () => import('@/views/app/MarketplaceClinicView.vue'), meta: { requiresFinancialAccess: true } },
  // D55-zh: Mi facturación integrada como tab dentro de Mi clínica. La ruta
  // /app/billing sigue accesible (deep links + checkout post-redirect con
  // ?session_id + ?simulated=1) — pasa los query a /app/config?tab=fact.
  {
    path: 'billing',
    redirect: (to) => ({ path: '/app/config', query: { ...to.query, tab: 'fact' } }),
  },
];

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'landing', component: () => import('@/views/public/LandingL1View.vue'), props: { preview: false } },
  { path: '/L1', name: 'landing-l1', component: () => import('@/views/public/LandingL1View.vue'), props: { preview: true } },
  { path: '/privacy', name: 'privacy', component: () => import('@/views/public/LegalView.vue') },
  { path: '/terms', name: 'terms', component: () => import('@/views/public/LegalView.vue') },
  { path: '/cookies', name: 'cookies', component: () => import('@/views/public/LegalView.vue') },
  { path: '/dpa', name: 'dpa', component: () => import('@/views/public/LegalView.vue') },
  { path: '/ayuda', name: 'ayuda', component: () => import('@/views/public/AyudaView.vue') },
  { path: '/instalar', name: 'instalar', component: () => import('@/views/public/InstallHelpView.vue') },
  { path: '/bienvenido', name: 'bienvenido', component: () => import('@/views/public/BienvenidoView.vue') },
  // Marketplace del tutor (Fase B) — standalone, se abre desde el link de WhatsApp
  // (?token=...). No usa el layout /app ni el guard de auth de clínica: el tutor
  // tiene su propia sesión (wiservet:tutor_access_token).
  { path: '/marketplace', name: 'marketplace-tutor', component: () => import('@/views/marketplace/TutorVitrinaView.vue') },
  // Panel del PROVEEDOR self-service (S3 P1) — ruta propia, fuera del shell de
  // clínica (/app). Sesión separada (wiservet:provider_access_token, ver
  // stores/providerAuth.ts) — NUNCA usa el guard requiresAuth de staff.
  { path: '/proveedor/ingresar', name: 'proveedor-login', component: () => import('@/views/marketplace/ProviderLoginView.vue') },
  { path: '/proveedor/activar', name: 'proveedor-activar', component: () => import('@/views/marketplace/ProviderActivateView.vue') },
  { path: '/proveedor', name: 'proveedor-dashboard', component: () => import('@/views/marketplace/ProviderDashboardView.vue'), beforeEnter: providerAuthGuard },
  { path: '/feedback/reabrir', name: 'feedback-reopen', component: () => import('@/views/public/FeedbackReopenView.vue') },
  { path: '/status', name: 'status', component: () => import('@/views/public/StatusView.vue') },
  { path: '/login', name: 'login', component: () => import('@/views/auth/LoginView.vue') },
  { path: '/forgot-password', name: 'forgot-password', component: () => import('@/views/auth/ForgotPasswordView.vue') },
  { path: '/reset-password', name: 'reset-password', component: () => import('@/views/auth/ResetPasswordView.vue') },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/views/onboarding/OnboardingView.vue'),
  },
  {
    path: '/app',
    component: () => import('@/components/layout/AppShell.vue'),
    props: { basePath: '/app' },
    meta: { requiresAuth: true },
    children: appChildren,
  },
  // Phase 16: /demo/* deprecated. Reroutes preservados para evitar 404 en
  // bookmarks/links viejos. Los users que entren a /demo/* van al onboarding
  // donde crean su cuenta; quienes ya tienen JWT van directo a /app/*.
  { path: '/demo/:rest(.*)?', redirect: '/onboarding' },
  { path: '/smoke', component: () => import('@/views/SmokeView.vue') },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// D77: cinturón extra al handler global `vite:preloadError` de main.ts —
// vue-router reporta acá los fallos de import() de componentes lazy durante
// una navegación (chunk con hash viejo tras un deploy). Guard 1×/sesión
// compartido con main.ts para no duplicar reloads.
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Unable to preload|error loading dynamically imported module|not a valid JavaScript MIME type/i;
router.onError((error, to) => {
  if (!CHUNK_ERROR_PATTERN.test(String((error as Error)?.message ?? error))) return;
  const key = 'wiservet:preload-error-reloaded';
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');
  void recoverFromChunkError('chunk-error-router', to?.fullPath);
});

// D75+1: la sesión podía "expirar" y dejar los paneles rotos con un error
// crudo en vez de mandar a /login — porque el redirect solo vivía en
// `beforeEach` (evaluado en cambio de ruta) y el usuario se quedaba en la
// misma pantalla viendo requests fallar. `api.ts` dispara este evento cuando
// detecta que la sesión ya no es recuperable (401 sin refresh, o 400
// NO_CLINIC_CONTEXT); acá forzamos la navegación sin importar el router
// dentro de api.ts (evitaría un ciclo api.ts→router→stores/auth→api.ts).
window.addEventListener('wiservet:session-expired', () => {
  // Esta señal es de la sesión de STAFF (services/api.ts). Si el usuario está
  // dentro de /proveedor (sesión de PROVEEDOR, completamente separada — ver
  // stores/providerAuth.ts), un 401 de staff no debe expulsarlo a /login: no
  // es su sesión. El caso real observado: un token de staff viejo/inválido en
  // sessionStorage dispara este evento durante el bootstrap global aunque el
  // usuario nunca inició sesión como staff, y lo mandaba a /login en vez de
  // dejarlo en /proveedor/ingresar (gap P3 hallado en revisión de seguridad).
  if (router.currentRoute.value.path.startsWith('/proveedor')) return;
  if (router.currentRoute.value.name === 'login') return;
  router.push({
    name: 'login',
    query: { redirect: router.currentRoute.value.fullPath, reason: 'session_expired' },
  });
});

// Señal de sesión de PROVEEDOR expirada/revocada (services/provider.ts, 401 en
// cualquier request autenticado — incluye Changelog v2 #2: session_version
// cambiado o status!=active). Mismo patrón que el listener de staff arriba,
// pero apunta a /proveedor/ingresar — nunca a /login (actor distinto).
window.addEventListener('wiservet:provider-session-expired', () => {
  if (router.currentRoute.value.name === 'proveedor-login') return;
  router.push({
    name: 'proveedor-login',
    query: { redirect: router.currentRoute.value.fullPath, reason: 'session_expired' },
  });
});

// Clave sessionStorage para el redirect de onboarding (una sola vez por sesión,
// evita loops si el usuario sale del wizard sin guardar credenciales).
const ONBOARDING_REDIRECT_KEY = 'wiservet:onboarding-redirect-done';

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.bootstrapped) await auth.bootstrap();
  if (to.matched.some((r) => r.meta.requiresAuth) && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (to.matched.some((r) => r.meta.requiresWiservetAdmin) && !auth.isWiservetAdmin) {
    return '/app/bandeja';
  }
  // §2.4 D72+4 — rutas 100% financieras: solo dueño/admin (canViewFinancials).
  if (to.matched.some((r) => r.meta.requiresFinancialAccess) && !auth.canViewFinancials) {
    return '/app/bandeja';
  }
  // §WF 2026-09-07 — modo financiero: bloquear vistas clínicas (sin CRM/visitas)
  // y mostrar un estado claro en vez del panel vacío "Sin datos".
  if (to.matched.some((r) => r.meta.blockedForFinancialOnly) && auth.isFinancialOnly) {
    return '/app/modo-financiero';
  }
  // Academia: acceso acotado (superadmin Wiservet o academyAllowlist).
  if (to.matched.some((r) => r.meta.requiresAcademyAccess) && !auth.canAccessAcademy) {
    return '/app/bandeja';
  }
  // Alta de clínicas: acceso acotado (superadmin Wiservet o onboarderAllowlist).
  if (to.matched.some((r) => r.meta.requiresOnboarderAccess) && !auth.canOnboardClinics) {
    return '/app/bandeja';
  }
  // Onboarding redirect suave: clínica trialing sin credenciales → wizard de configuración.
  // Solo se ejecuta una vez por sesión (sessionStorage flag) para no atrapar al usuario
  // en el wizard si sale sin completarlo.
  // Excepciones: ya está en /conectar, es admin Wiservet, o ya se redirigió en esta sesión.
  if (
    auth.isAuthenticated &&
    !auth.isWiservetAdmin &&
    to.path.startsWith('/app') &&
    to.path !== '/app/conectar' &&
    !auth.hasCredentials &&
    !sessionStorage.getItem(ONBOARDING_REDIRECT_KEY)
  ) {
    const clinic = auth.clinic;
    if (clinic?.subscription_status === 'trialing') {
      sessionStorage.setItem(ONBOARDING_REDIRECT_KEY, '1');
      return '/app/conectar';
    }
  }
  return true;
});

// Medición de uso propia (plan de enganche F0, 27-oct-2026): una vista por
// pantalla de /app, con el PATRÓN de la ruta (sin ids ni query). Sin terceros.
router.afterEach((to, _from, failure) => {
  if (failure || !to.path.startsWith('/app')) return;
  if (!useAuthStore().isAuthenticated) return;
  const rec = to.matched[to.matched.length - 1];
  trackPageView(rec?.path || to.path);
});
